<script lang="ts">
  /**
   * The status chip (brief §14a.8).
   *
   * Small, at the top, always in the same place. It is the *entire* offline user interface:
   * there is no error dialogue, no red bar and no "you are offline" modal, because being
   * offline is a state and not a mistake anybody made (brief §14a.1).
   *
   * The one thing it does insist on is the error list. A validation failure or a rate limit
   * is the user's to see — once — and it stays tappable until they have.
   */
  import { MAX_ATTEMPTS, outbox } from './outbox.svelte.ts';

  type Props = { offline: boolean; stale?: string | undefined };
  const { offline, stale }: Props = $props();

  let showErrors = $state(false);

  const label = $derived.by(() => {
    const status = outbox.status;
    if (status.kind === 'failed') {
      return `${status.failed} nicht übernommen`;
    }
    if (status.kind === 'syncing') return `Synchronisiere ${status.pending} …`;
    if (status.kind === 'done') {
      // What the reports among them earned (amendment 21), for the three seconds the chip shows.
      const earned = status.coins > 0 ? ` · +${status.coins} 🪙` : '';
      return `${status.applied} übernommen${earned}`;
    }
    if (status.kind === 'waiting') {
      return offline ? `Offline · ${status.pending} warten` : `${status.pending} warten`;
    }
    if (offline) return 'Offline — Karte aus dem Cache';
    return stale;
  });

  const KIND_LABELS: Record<string, string> = {
    sighting: 'Meldung',
    confirm: 'Bestätigung',
    passby: 'Bewertung',
    settings: 'Einstellungen',
    report: 'Beschwerde',
  };
</script>

{#if label !== undefined && label !== ''}
  <div class="wrap">
    <button
      class="chip"
      class:warn={outbox.status.kind === 'failed'}
      onclick={() => (showErrors = outbox.status.kind === 'failed' && !showErrors)}
      aria-live="polite"
    >
      {label}
    </button>

    {#if outbox.needsSignIn}
      <!-- Brief §14a.5: a 401 never clears the queue. It asks, and keeps everything. -->
      <span class="chip warn">Bitte neu anmelden — nichts geht verloren</span>
    {/if}
  </div>
{/if}

{#if showErrors && outbox.failed.length > 0}
  <div class="errors" role="alertdialog" aria-label="Nicht übernommen">
    <ul>
      {#each outbox.failed as op (op.opId)}
        <li>
          <strong>{KIND_LABELS[op.kind] ?? op.kind}</strong>
          <span>{op.lastError ?? 'abgelehnt'}</span>
          {#if op.attempts >= MAX_ATTEMPTS}
            <span class="tries">nach {op.attempts} Versuchen</span>
          {/if}
        </li>
      {/each}
    </ul>
    <div class="actions">
      <button onclick={() => outbox.retryFailed()}>Nochmal versuchen</button>
      <button
        class="quiet"
        onclick={() => {
          outbox.dismissFailed();
          showErrors = false;
        }}
      >
        Verwerfen
      </button>
    </div>
  </div>
{/if}

<style>
  /* Placed by the banner lane in App.svelte, not by itself: two overlays that both put
     themselves at the top centre are two overlays in the same pixels. */
  .wrap {
    display: flex;
    gap: 0.4rem;
    max-width: 100%;
  }

  .chip {
    padding: 0.3rem 0.75rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--card);
    color: var(--muted);
    font-size: 0.75rem;
    white-space: nowrap;
    cursor: default;
  }

  .chip.warn {
    border-color: var(--accent);
    color: var(--accent);
    cursor: pointer;
  }

  /* `fixed`, not `absolute`: this is a dialogue and belongs to the viewport. Absolute would
     now resolve against the banner lane, which is a chip-sized box near the top edge. */
  .errors {
    position: fixed;
    left: 50%;
    top: max(3rem, calc(env(safe-area-inset-top) + 2.5rem));
    transform: translateX(-50%);
    z-index: 4;
    width: min(24rem, calc(100vw - 2rem));
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--accent);
    border-radius: 0.75rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 30%);
  }

  ul {
    display: grid;
    gap: 0.4rem;
    margin: 0 0 0.6rem;
    padding: 0;
    list-style: none;
    font-size: 0.8125rem;
  }

  li {
    display: grid;
    gap: 0.1rem;
  }

  li span {
    color: var(--muted);
  }

  .tries {
    font-size: 0.6875rem;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
  }

  .actions button {
    flex: 1;
    padding: 0.45rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .quiet {
    opacity: 0.7;
  }
</style>
